import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import { Button, Input } from 'components/form';
import SelectionCheckbox from 'components/form/SelectionCheckbox';
import { Row } from 'components/layout';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { Option } from 'lib/models/option';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import SidebarMP from '../SidebarMP';
import MobileHeaderMp from '../MobileHeaderMp';

import styles from '../Onboarding.module.scss';

export interface LanguagesProps {
  /** Custom class name */
  className?: string;
  languages: Array<Option>;
  candidateChoice: Array<string>;
  defaultLanguage: string;
  showLanguageCount?: number;
  onNextClick: (
    selectedLanguages: Array<string>,
    isBackClicked?: boolean,
    showCount?: number
  ) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  isMatchingPreferences?: boolean;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const Languages: React.FC<LanguagesProps> = memo(
  ({
    className = '', // custom class name
    languages,
    candidateChoice,
    defaultLanguage,
    onNextClick,
    showLanguageCount,
    isLoading,
    isBackLoading,
    isMatchingPreferences,
    onStepSelect,
  }: LanguagesProps) => {
    const [t] = useTranslation('candidate-onboarding');
    const [tCommon] = useTranslation('common');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInfo } = useCandidate();

    const contentClassNames = cn(styles.container, className);
    const [scrolled, setScrolled] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [search, setSearch] = useState('');
    const [showCount, setShowCount] = useState(showLanguageCount || 15);

    const containerElement = containerRef.current;
    const [selectedLanguages, setSelectedLanguages] = useState<Array<string>>(
      []
    );

    useEffect(() => {
      if (candidateChoice && candidateChoice?.length > 0)
        setSelectedLanguages(candidateChoice);
      else setSelectedLanguages([defaultLanguage]);
    }, [candidateChoice, defaultLanguage]);

    const isReady = !!selectedLanguages.length;
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding({ isReady });

    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };

    const apiCallToUpdate = async (modifiedLanguage: Array<string>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            language_ids: modifiedLanguage,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedLanguage) => {
        await apiCallToUpdate(modifiedLanguage);
      }, 3000)
    ).current;

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      setShowCount(showCount);
      onNextClick(selectedLanguages, isBackClicked, showCount);
    };

    const onLanguagesCheckboxClick = (id: string) => {
      setValueEdited(true);
      const index = selectedLanguages?.findIndex((type) => type === id);
      let modifiedLanguage = [];
      if (index === -1) modifiedLanguage = [...selectedLanguages, id];
      else
        modifiedLanguage =
          selectedLanguages && selectedLanguages.filter((item) => item !== id);

      /** For MP
       * Desktop: Autosave on every change
       *          Do not make changes when nothing is selected on mandatory step
       * Mobile: Save on CTA click
       */
      setSelectedLanguages(modifiedLanguage);
      if (modifiedLanguage.length === 0) return;

      if (isMatchingPreferences && !screens.sm) {
        debouncedAPICall(modifiedLanguage);
      }
    };

    useEffect(() => {
      if (!containerElement) return;

      function handler() {
        if (!containerElement) return;

        setScrolled(containerElement.scrollTop > 50);
      }
      containerElement.addEventListener('scroll', handler);

      return () => {
        containerElement.removeEventListener('scroll', handler);
      };
    }, [containerElement]);

    const trimmedSearch = search.trim();
    const filteredOptions = useMemo(() => {
      return search
        ? languages?.filter(
            (resp) =>
              resp.label.toLowerCase().indexOf(trimmedSearch.toLowerCase()) > -1
          )
        : languages;
    }, [trimmedSearch, languages, search]);

    const hasMoreRecords = useMemo(() => {
      return showCount < filteredOptions?.length;
    }, [showCount, filteredOptions]);
    if (!languages) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.languages')}
          onChevronClick={onChevronClick}
        />
        <div
          className={contentClassNames}
          data-testid="Languages"
          ref={containerRef}
        >
          {isMatchingPreferences && onStepSelect && (
            <SidebarMP
              isReady={isReady}
              className={styles.sidebar}
              onStepSelect={onStepSelect}
            />
          )}
          <div
            className={cn(styles.content, {
              [styles['content-mp']]: isMatchingPreferences,
            })}
          >
            <p className={styles.setup}>{t('language.title')}</p>
            <p className={styles.desc}>{t('language.subtitle')}</p>
            <Input
              className={cn(styles.search, {
                [styles.sticky]: scrolled,
              })}
              placeholder={tCommon('search')}
              startIcon="search"
              startIconSize="small"
              endIconSize="xsmall"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endIcon={search ? 'cancel' : undefined}
              onEndIconClick={() => setSearch('')}
            />
            <div className={styles.checkboxesResponsibility}>
              {filteredOptions.slice(0, showCount).map((language) => (
                <SelectionCheckbox
                  key={language.id}
                  checked={selectedLanguages?.some((id) => language.id === id)}
                  onChange={() => onLanguagesCheckboxClick(language.id)}
                  className={styles.checkboxResponsibility}
                >
                  {language.label}
                </SelectionCheckbox>
              ))}
            </div>

            <Row align="center" columnGap={8} className={styles.countsRow}>
              <p>
                {t('showing-count', {
                  count: Math.min(showCount, filteredOptions?.length),
                  totalCount: filteredOptions?.length,
                })}
              </p>
              {hasMoreRecords ? (
                <Button
                  variant="text"
                  color="blue"
                  onClick={() => {
                    setShowCount(filteredOptions.length);
                  }}
                  className={styles.countsButton}
                >
                  {t('show-all')}
                </Button>
              ) : null}
            </Row>
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>

          <Footer
            isReady={isReady}
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            onSubmit={onSubmit}
            isFullyScrolled={fullyScrolled}
            isWindowScrolled={isWindowScrolled}
            isMatchingPreference={isMatchingPreferences}
          />
        </div>
      </div>
    );
  }
);

Languages.displayName = 'Languages';
